import { Injectable, inject, computed, signal, isDevMode, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AVAILABLE_LANGUAGES, Language } from '../models/language.model';

const LANGUAGE_KEY = 'preferredLanguage';

@Injectable()
export class LanguageService {
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private availableLanguages = AVAILABLE_LANGUAGES;

  private _currentLanguage = signal<Language>(this.getInitialLanguage());

  currentLanguage = computed(() => this._currentLanguage());

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    // Only redirect in production on deployed domains (not localhost/emulator)
    const isLocalhost = window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1';

    // Check if Angular i18n routing is active (base href has locale prefix)
    const baseHref = this.document.querySelector('base')?.getAttribute('href') || '/';
    const hasLocalizedRouting = baseHref.match(/^\/(de|en)\//);

    if (!isDevMode() && !isLocalhost && hasLocalizedRouting) {
      this.checkAndRedirect();
    }
  }

  private getInitialLanguage(): Language {
    if (isPlatformBrowser(this.platformId)) {
      const storedLang = localStorage.getItem(LANGUAGE_KEY);
      if (storedLang) {
        const lang = this.availableLanguages.find((l) => l.code === storedLang);
        if (lang) return lang;
      }
    }

    // Use document locale (works on both server and browser)
    const currentLocale = this.document.documentElement.lang;
    const lang = this.availableLanguages.find((l) => l.code === currentLocale);

    return lang || this.availableLanguages[0];
  }

  private checkAndRedirect(): void {
    try {
      const currentPath = window.location.pathname;
      const currentLangMatch = currentPath.match(/^\/(de|en)/);
      const currentLangCode = currentLangMatch ? currentLangMatch[1] : null;
      const storedLang = localStorage.getItem(LANGUAGE_KEY);

      // Determine target language using priority order:
      // 1. URL language code (explicit navigation - highest priority)
      // 2. localStorage preference (user's saved choice)
      // 3. Browser language (fallback - only if neither URL nor localStorage exist)
      let targetLang: string;

      if (
        currentLangCode &&
        this.availableLanguages.some((l) => l.code === currentLangCode)
      ) {
        // Priority 1: URL has valid language code - use it (handles cross-app navigation)
        targetLang = currentLangCode;
      } else if (
        storedLang &&
        this.availableLanguages.some((l) => l.code === storedLang)
      ) {
        // Priority 2: localStorage has valid preference - use it
        targetLang = storedLang;
      } else {
        // Priority 3: No URL language and no localStorage - check browser language
        const browserLangs = navigator.languages || [navigator.language];
        targetLang = 'de'; // Default to German

        for (const browserLang of browserLangs) {
          const langCode = browserLang.split('-')[0]; // Extract 'de' from 'de-DE'
          if (this.availableLanguages.some((l) => l.code === langCode)) {
            targetLang = langCode;
            break;
          }
        }
      }

      // Save target language to localStorage
      localStorage.setItem(LANGUAGE_KEY, targetLang);

      // Redirect if current URL doesn't match target language
      if (currentLangCode !== targetLang) {
        const newPath = `/${targetLang}${
          currentPath.startsWith('/de') || currentPath.startsWith('/en')
            ? currentPath.substring(3)
            : currentPath
        }`;
        const queryString = window.location.search;
        window.location.replace(`${newPath}${queryString}`);
      }
    } catch (error) {
      console.error('[LanguageService] Error in checkAndRedirect:', error);
      // Don't throw - allow app to load even if language detection fails
    }
  }

  setLanguage(language: Language): void {
    if (language.code === this.currentLanguage().code) return;
    if (!isPlatformBrowser(this.platformId)) return;

    localStorage.setItem(LANGUAGE_KEY, language.code);
    this._currentLanguage.set(language);

    const currentPath = window.location.pathname;
    const newPath = `/${language.code}${
      currentPath.startsWith('/de') || currentPath.startsWith('/en')
        ? currentPath.substring(3)
        : currentPath
    }`;
    const queryString = window.location.search;

    // Force complete page reload with cache busting for Angular i18n
    // This is necessary because each locale has separate bundle files
    window.location.replace(`${newPath}${queryString}`);
  }

  getLanguages(): Language[] {
    return this.availableLanguages;
  }
}
